import React, { useEffect, useMemo, useState } from 'react';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { Workflow } from 'lucide-react';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { DatasetModel } from './dataset.model';

const expandWorkflowsParamsSchema = z.object({
  /** Ids of attached workflows to expand into output columns. */
  workflowIds: z.array(z.string().min(1)),
  // A row kebab menu already knows which dataset it targets, so it can pass the
  // id directly rather than relying on the shared selection model.
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof expandWorkflowsParamsSchema>;

type Errors = { t: string; message?: string }[];

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type ExpandWorkflowsActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'updateSettings'>;
  };
};

/**
 * Expands a dataset's attached workflows into grid columns, one per workflow,
 * each cell showing that workflow's most-recent run output for the row.
 *
 * The choice of which workflows to expand is saved on the dataset — a shared
 * view setting, the same for everyone who opens it — so this writes through the
 * settings route like a rename does. The output values themselves already ride
 * on the automation overlay the grid loads; this only decides which become
 * columns.
 */
export class ExpandWorkflowsAction extends BaseAction<Params, Errors> {
  title = 'Expand workflows into columns';
  description =
    'Show each attached workflow’s latest output as a column in the grid';
  inputSchema = expandWorkflowsParamsSchema;
  group = 'Dataset';
  icon = 'workflow';

  constructor(
    private readonly client: ExpandWorkflowsActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    // Prefer the page's model when it is the one this targets: it creates the
    // dataset first if it somehow has not been saved, and broadcasts the update
    // itself. Otherwise write straight through and announce it, the way the
    // row-keys action does from a resource list.
    const model = context.targetedModelOfType(DatasetModel);
    if (model !== null && model.datasetId === datasetId) {
      const result = await model.persistExpandedAutomationWorkflowIds(
        payload.workflowIds
      );
      if (result.isErr()) {
        toast.error(result.error);
        return err([{ t: 'error', message: result.error }]);
      }
    } else {
      const result = await this.client.resource.dataset.updateSettings({
        id: datasetId,
        expandedAutomationWorkflowIds: payload.workflowIds,
      });
      if (result.isErr()) {
        const message =
          result.error.t === 'application'
            ? result.error.err.message ?? 'Failed to expand workflows'
            : 'Failed to expand workflows';
        toast.error(message);
        return err([{ t: 'error', message }]);
      }
      emit(BroadcastEventType.ResourceUpdated, {
        orgId: this.orgId,
        resourceType: 'dataset',
        resourceId: datasetId,
      });
    }

    toast.success(
      payload.workflowIds.length === 0
        ? 'Workflow columns hidden'
        : `Showing ${payload.workflowIds.length} workflow ${
            payload.workflowIds.length === 1 ? 'column' : 'columns'
          }`
    );
    return ok({});
  }

  ask(params: Partial<Params>, context: ExecuteContext): AskResponse<Params> {
    // Resolved before the modal opens, for the same reason the row-keys action
    // resolves it here: the shared selection can move while the dialog is up.
    const datasetId = params.datasetId ?? this.getTargetDatasetId(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          view: () => (
            <ExpandWorkflowsAskView
              datasetId={datasetId}
              onRespond={async (nextParams) => {
                await onRespond({
                  ...nextParams,
                  ...(datasetId !== null ? { datasetId } : {}),
                });
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private selectedDataset(
    context: ApplicableContext
  ): ResourceSelectionModel | null {
    if (!context.isInScope(ResourceSelectionModel)) {
      return null;
    }
    const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
    const single = selection.length === 1 ? selection[0] : undefined;
    if (
      single === undefined ||
      single.selections.length !== 1 ||
      single.getResourceIdsForType(ResourceType.Dataset).length !== 1
    ) {
      return null;
    }
    return single;
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const selected = this.selectedDataset(context);
    if (selected !== null) {
      return selected.getResourceIdsForType(ResourceType.Dataset)[0] ?? null;
    }
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }
}

type WorkflowOption = { id: string; name: string };

/**
 * Lists the workflows attached to the dataset and lets the user pick which to
 * expand into columns.
 *
 * Fetches the wiring and the dataset itself rather than reading a model, so the
 * same dialog works from the dataset page, a resource list row and the command
 * palette alike. The attached-workflow list is exactly the automations wiring
 * the grid's Workflows column is built from.
 */
const ExpandWorkflowsAskView: React.FC<{
  datasetId: string | null;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ datasetId, onRespond, onCancel }) => {
  const client = useTenantedClient();
  const [workflows, setWorkflows] = useState<WorkflowOption[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (datasetId === null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const [wiringRes, datasetRes] = await Promise.all([
        client.resource.workflow.datasetAutomations({ datasetId }),
        client.resource.get({ type: ResourceType.Dataset, id: datasetId }),
      ]);
      if (cancelled) {
        return;
      }
      if (wiringRes.isErr()) {
        setLoadError('Could not load the workflows on this dataset');
        return;
      }
      // A workflow can be bound through more than one trigger; it is still one
      // column, so it appears once. First binding wins the name.
      const seen = new Set<string>();
      const options: WorkflowOption[] = [];
      for (const trigger of wiringRes.value.triggers) {
        for (const workflow of trigger.workflows) {
          if (seen.has(workflow.id)) {
            continue;
          }
          seen.add(workflow.id);
          options.push({ id: workflow.id, name: workflow.name });
        }
      }
      setWorkflows(options);
      const savedIds =
        !datasetRes.isErr() && datasetRes.value.type === 'dataset'
          ? datasetRes.value.dataset.expandedAutomationWorkflowIds ?? []
          : [];
      // Only ids that still name a bound workflow, so a stale saved id does not
      // leave the dialog with a selection the list cannot show.
      setSelected(savedIds.filter((id) => seen.has(id)));
    })();
    return () => {
      cancelled = true;
    };
  }, [client, datasetId]);

  const toggle = (id: string): void => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : [...current, id]
    );
  };

  const canSubmit = useMemo(
    () => !saving && datasetId !== null && workflows !== null,
    [saving, datasetId, workflows]
  );

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    setSaving(true);
    await onRespond({ workflowIds: selected });
    setSaving(false);
  };

  return (
    <ModalPageView
      title="Expand workflows into columns"
      subtitle="Each selected workflow becomes a column showing its latest output per row"
      className="px-4 py-4"
    >
      <div className="flex flex-col gap-4">
        {loadError !== null ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : workflows === null ? (
          <div className="flex justify-center py-6">
            <Loading.Spinner variant="sm" />
          </div>
        ) : workflows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No workflows are attached to this dataset yet. Attach one from the
            Workflows column to expand its output here.
          </p>
        ) : (
          <div className="flex max-h-[min(40vh,20rem)] flex-col gap-2 overflow-y-auto">
            {workflows.map((workflow) => (
              <label
                key={workflow.id}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted/40"
              >
                <Checkbox
                  checked={selected.includes(workflow.id)}
                  onCheckedChange={() => toggle(workflow.id)}
                />
                <Workflow className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium">
                  {workflow.name}
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {saving ? <Loading.Spinner variant="sm" /> : 'Save columns'}
          </Button>
        </div>
      </div>
    </ModalPageView>
  );
};
